import React, { useState, useEffect } from 'react';
import { XMarkIcon, CalculatorIcon } from '@heroicons/react/24/outline';

interface ROICalculatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentValues: {
    hourlyRate: number;
    hoursPerNewFlow: number;
    hoursPerFlowUpdate: number;
  };
  onSave: (values: {
    hourlyRate: number;
    hoursPerNewFlow: number;
    hoursPerFlowUpdate: number;
  }) => void;
}

export function ROICalculatorModal({ 
  isOpen, 
  onClose, 
  currentValues, 
  onSave 
}: ROICalculatorModalProps) {
  const [hourlyRate, setHourlyRate] = useState(currentValues.hourlyRate);
  const [hoursPerNewFlow, setHoursPerNewFlow] = useState(currentValues.hoursPerNewFlow);
  const [hoursPerFlowUpdate, setHoursPerFlowUpdate] = useState(currentValues.hoursPerFlowUpdate);

  // Update form when currentValues change
  useEffect(() => {
    setHourlyRate(currentValues.hourlyRate);
    setHoursPerNewFlow(currentValues.hoursPerNewFlow);
    setHoursPerFlowUpdate(currentValues.hoursPerFlowUpdate);
  }, [currentValues]);

  const handleSave = () => {
    onSave({
      hourlyRate,
      hoursPerNewFlow,
      hoursPerFlowUpdate
    });
  };

  const handleRestoreDefaults = () => {
    setHourlyRate(85);
    setHoursPerNewFlow(1.5);
    setHoursPerFlowUpdate(0.75);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CalculatorIcon className="h-5 w-5 text-gray-700" />
              <h3 className="text-lg font-semibold text-gray-900">ROI Calculator Settings</h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-gray-600">
            Customize how FlowDocs calculates your return on investment by adjusting these values.
          </p>

          <div>
            <label htmlFor="hourlyRate" className="block text-sm font-medium text-gray-700">
              Hourly Rate ($)
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">$</span>
              </div>
              <input
                type="number"
                id="hourlyRate"
                min="0"
                step="0.01"
                value={hourlyRate}
                onChange={(e) => setHourlyRate(parseFloat(e.target.value) || 0)}
                className="pl-7 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="85.00"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Average hourly cost of technical documentation
            </p>
          </div>

          <div>
            <label htmlFor="hoursPerNewFlow" className="block text-sm font-medium text-gray-700">
              Hours to Document New Flow
            </label>
            <input
              type="number"
              id="hoursPerNewFlow"
              min="0"
              step="0.01"
              value={hoursPerNewFlow}
              onChange={(e) => setHoursPerNewFlow(parseFloat(e.target.value) || 0)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              placeholder="1.5"
            />
            <p className="mt-1 text-xs text-gray-500">
              Time saved documenting a flow from scratch
            </p>
          </div>

          <div>
            <label htmlFor="hoursPerFlowUpdate" className="block text-sm font-medium text-gray-700">
              Hours to Document Flow Changes
            </label>
            <input
              type="number"
              id="hoursPerFlowUpdate"
              min="0"
              step="0.01"
              value={hoursPerFlowUpdate}
              onChange={(e) => setHoursPerFlowUpdate(parseFloat(e.target.value) || 0)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              placeholder="0.75"
            />
            <p className="mt-1 text-xs text-gray-500">
              Time saved updating documentation for flow changes
            </p>
          </div>

          <div className="pt-2">
            <button
              onClick={handleRestoreDefaults}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Restore Defaults
            </button>
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}